/**
 * Anonymous → account migration planning.
 *
 * Deliberately pure functions over plain data, with no IndexedDB and no fetch,
 * because the rules here are where the damage happens: uploading the wrong
 * person's work, losing designs to a quota, or restarting an upload that
 * already succeeded. Keeping them free of I/O makes them exhaustively testable.
 *
 * See docs/decisions/sign-in.md.
 */
import type { GarmentType } from "./format";

export interface LocalProjectMeta {
  id: string;
  title: string;
  /** Last edit, ms. Drives which projects win a contested quota. */
  updatedAt: number;
  /**
   * Which account has taken ownership of this local project, or null if it is
   * still unowned.
   *
   * Local projects are device-scoped. On a shared device we cannot know whose
   * work is sitting there, so it stays unowned until somebody explicitly
   * claims it — auto-uploading would silently move one person's designs into
   * another's account.
   */
  claimedBy: string | null;
  /** Set once the upload has completed. Presence means "do not upload again". */
  migratedTo?: { userId: string; projectId: string };
  sizeBytes: number;
  /**
   * Shirt or pants.
   *
   * A copy of the archive's `manifest.garmentType`, kept here so a list of
   * designs can say what each one is without unzipping every archive to find
   * out. The two templates are geometrically identical, so nothing about the
   * pixels can answer this and nothing else on the card can either.
   *
   * Optional because rows written before the editor could make pants do not
   * have it. Absent means unknown, not "shirt" — defaulting would have quietly
   * mislabelled the one design on this device that was already pants.
   */
  garment?: GarmentType;
  /**
   * A small WebP of the composite, for the designs grid.
   *
   * On the metadata row rather than in the archive, because the list reads only
   * metadata — fetching a 200 KB archive to show a 12 KB picture would undo the
   * whole reason the two stores are separate. Absent on rows written before
   * thumbnails existed; those show their colour tint until the next save, the
   * same way `garment` filled itself in.
   */
  thumb?: Uint8Array;
}

export interface MigrationPlan {
  /** Within the remaining quota; upload these. Most recently edited first. */
  upload: LocalProjectMeta[];
  /** Over quota. They stay local and the user is told, never silently dropped. */
  deferred: LocalProjectMeta[];
  quotaRemaining: number;
}

/**
 * Which local projects may be offered to this user.
 *
 * Unowned projects are offerable to anyone. Projects claimed by this user but
 * not yet migrated are still offerable — that is a half-finished migration
 * being resumed, not a duplicate. Projects claimed by anyone else are never
 * offered, and projects already migrated are done.
 */
export function claimableFor(
  projects: readonly LocalProjectMeta[],
  userId: string,
): LocalProjectMeta[] {
  return projects.filter((p) => {
    if (p.migratedTo) return false;
    return p.claimedBy === null || p.claimedBy === userId;
  });
}

/**
 * Split claimable projects into what fits the remaining quota and what does not.
 *
 * Most recently edited wins, because that is what someone is most likely to
 * care about and least likely to be able to reconstruct.
 */
export function planMigration(
  claimable: readonly LocalProjectMeta[],
  quotaRemaining: number,
): MigrationPlan {
  const ordered = [...claimable].sort((a, b) => b.updatedAt - a.updatedAt);
  const capacity = Math.max(0, quotaRemaining);
  return {
    upload: ordered.slice(0, capacity),
    deferred: ordered.slice(capacity),
    quotaRemaining: capacity,
  };
}

/**
 * What is left to do, given what already succeeded.
 *
 * Migration is per-project and resumable, never atomic: a phone connection that
 * dies after three of five uploads must not roll back the three that are
 * already safe.
 */
export function pendingUploads(
  plan: MigrationPlan,
  completedIds: readonly string[],
): LocalProjectMeta[] {
  const done = new Set(completedIds);
  return plan.upload.filter((p) => !done.has(p.id));
}

export interface MigrationSummary {
  total: number;
  moved: number;
  failed: number;
  deferred: number;
  complete: boolean;
}

/** Honest reporting. Partial success is a real outcome, not a failure. */
export function summarise(
  plan: MigrationPlan,
  completedIds: readonly string[],
  failedIds: readonly string[],
): MigrationSummary {
  const moved = plan.upload.filter((p) => completedIds.includes(p.id)).length;
  const failed = plan.upload.filter((p) => failedIds.includes(p.id)).length;
  return {
    total: plan.upload.length,
    moved,
    failed,
    deferred: plan.deferred.length,
    complete: moved === plan.upload.length,
  };
}

/** Human-readable outcome. Never implies work was lost when it was not. */
export function describeSummary(s: MigrationSummary): string {
  if (s.total === 0 && s.deferred === 0) return "Nothing to move.";
  if (s.complete && s.deferred === 0) {
    return `Moved ${s.moved} design${s.moved === 1 ? "" : "s"} to your account.`;
  }
  const parts: string[] = [];
  if (s.moved > 0) parts.push(`Moved ${s.moved} of ${s.total}`);
  if (s.failed > 0) parts.push(`${s.failed} didn't upload — you can retry`);
  if (s.deferred > 0) {
    parts.push(
      `${s.deferred} stayed on this device — your plan holds ${s.total} saved design${
        s.total === 1 ? "" : "s"
      }`,
    );
  }
  return `${parts.join(". ")}.`;
}
